import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, InputAdornment, TextField } from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { api, type Announcement, type DeckSummary } from '../../lib/api';
import { navigate, paths } from '../../lib/router';
import { useDepartmentStore } from '../../store/departmentStore';
import { AnnouncementBoard } from './AnnouncementBoard';
import { DeckCard, DeckGridSkeleton, EmptyState, PageHeader, Section } from './portalParts';
import { deckGrid } from './portalStyles';

/** A department folder: its announcements and the decks shared into it. */
export function DepartmentPage({ id }: { id: string }) {
  const department = useDepartmentStore((s) => s.departments.find((d) => d.id === id));
  const loaded = useDepartmentStore((s) => s.loaded);
  const [announcements, setAnnouncements] = useState<Announcement[] | null>(null);
  const [decks, setDecks] = useState<DeckSummary[] | null>(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const loadAnnouncements = useCallback(
    () =>
      api.announcements
        .list()
        .then((r) => setAnnouncements(r.announcements.filter((a) => a.category === id)))
        .catch((err) => setError(err instanceof Error ? err.message : 'Could not load announcements')),
    [id],
  );

  useEffect(() => {
    void loadAnnouncements();
    api
      .listDecks('shared')
      .then((r) => setDecks(r.decks.filter((d) => d.category === id)))
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load decks'));
  }, [id, loadAnnouncements]);

  if (loaded && !department) {
    return <Alert severity="warning">This department folder doesn't exist any more.</Alert>;
  }

  const q = query.trim().toLowerCase();
  const filtered = decks && decks.filter((d) => !q || d.title.toLowerCase().includes(q) || d.owner_name.toLowerCase().includes(q));

  return (
    <>
      <PageHeader
        icon={
          <Box sx={{ display: 'grid', placeItems: 'center', width: 48, height: 48, borderRadius: 2.5, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
            <FolderOutlinedIcon sx={{ color: department?.color, fontSize: 26 }} />
          </Box>
        }
        title={department?.name ?? ' '}
        description={department?.description || 'Department folder'}
      />
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <AnnouncementBoard
        title="Announcements"
        icon={<CampaignOutlinedIcon />}
        items={announcements}
        onChanged={() => void loadAnnouncements()}
        defaultCategory={id}
        emptyText={`No ${department?.name ?? ''} announcements yet.`}
      />

      <Section
        title="Shared decks"
        icon={<PeopleOutlineIcon />}
        action={
          decks && decks.length > 3 ? (
            <TextField
              placeholder="Search this folder"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ width: 240 }}
              slotProps={{
                htmlInput: { 'aria-label': 'Search this folder' },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          ) : undefined
        }
      >
        {filtered === null ? (
          <DeckGridSkeleton />
        ) : filtered.length ? (
          <Box sx={deckGrid}>
            {filtered.map((d) => (
              <DeckCard key={d.id} deck={d} onOpen={() => navigate(paths.view(d.id))} />
            ))}
          </Box>
        ) : (
          <EmptyState>
            {q
              ? `No decks match “${query}”.`
              : `No decks in this folder yet. Open a deck, click Share and pick ${department?.name ?? 'this department'}.`}
          </EmptyState>
        )}
      </Section>
    </>
  );
}
